import secondaryComponents from "../../../../components/secondaryComponents";
import { motion } from "framer-motion";
import styles from "./ExpenseDetails.module.scss";
// import useExpenses from "./useExpenses";
import primaryComponents from "../../../../components/primaryComponents";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { defaultDateDisplay, formatCurrency } from "../../../../helpers";
import { Link, useNavigate } from "react-router-dom";
import useExpenseDetails from "./useExpenseDetails";
import globalStyles from "../../../../assets/styles/base/globalPage.module.scss";
import { pathConstants } from "../../../../routes/pathContants";
import { RoutedModal, WarningDialog } from "../../../../components/common";

const ExpenseDetails = () => {
  const navigate = useNavigate();
  const {
    isFetchingExpenseDetails,
    expenseDetails,
    isReviewExpense,
    setIsReviewExpense,
    handleCancelRecurrence,
    isCancellingRecurrence,
    isConfirmingCancelRecurrence,
    setIsConfirmingCancelRecurrence,
  } = useExpenseDetails();

  return (
    <secondaryComponents.MetaSetter
      title="Lumi Merchant | Expense Details"
      description="View expense details"
    >
      <secondaryComponents.RequestLoaderTrigger>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: { duration: 0.3 } }}
          exit={{ opacity: 0 }}
          className={`${styles.expenseDetails} ${globalStyles?.pageColumnGaps} ${globalStyles.pagePaddings}`}
        >
          <div>
            <primaryComponents.Button
              classNames="btn btn--outline"
              onClick={() => navigate(-1)}
            >
              <FontAwesomeIcon
                icon="angle-left"
                className="mr-3"
                style={{ fontSize: "18px" }}
              />
              <span>Back</span>
            </primaryComponents.Button>
          </div>

          <secondaryComponents.LoaderHelper
            classNames="mt-5"
            isLoading={isFetchingExpenseDetails}
          >
            <div className={styles.expenseDetails__header}>
              <div>
                <h3 style={{ maxWidth: "600px" }}>{expenseDetails?.name}</h3>
                {(expenseDetails?.is_recurring ||
                  expenseDetails?.source === "recurring") && (
                  <div
                    className="d-flex align-items-center flex-wrap mt-2"
                    style={{ gap: "10px" }}
                  >
                    {expenseDetails?.is_recurring && (
                      <span className="purple--squared">
                        <FontAwesomeIcon icon="redo-alt" className="mr-2" />
                        Recurring
                        {expenseDetails?.recurrence_frequency
                          ? ` (${expenseDetails.recurrence_frequency})`
                          : ""}
                      </span>
                    )}
                    {expenseDetails?.source === "recurring" && (
                      <span className="gray--squared">System-generated</span>
                    )}
                    {expenseDetails?.recurrence_cancelled_at && (
                      <span className="error--squared">
                        Recurrence cancelled
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="d-flex ml-auto flex-wrap" style={{ gap: "10px" }}>
                <primaryComponents.Button
                  classNames="btn btn--outline ml-auto"
                  isDisabled={!expenseDetails?.proof_url}
                  onClick={() =>
                    window.open(expenseDetails?.proof_url, "_blank")
                  }
                >
                  <FontAwesomeIcon icon="download" className="mr-2" />
                  <span> Downlaod Proof Of Expense</span>
                </primaryComponents.Button>
                <primaryComponents.Button
                  classNames="btn btn--primary ml-auto"
                  onClick={() => setIsReviewExpense(true)}
                >
                  <span> Review</span>
                </primaryComponents.Button>
              </div>
            </div>

            <div>
              <h4>Expense Details</h4>
              <div className={styles.expenseDetails__card}>
                <div>
                  <h5>Sub Total</h5>
                  <p>
                    {formatCurrency({
                      value: expenseDetails?.sub_amount,
                      currencyCode: expenseDetails?.store?.currency_code,
                    })}
                  </p>
                </div>
                <div>
                  <h5>VAT Amount</h5>
                  <p>
                    {formatCurrency({
                      value: expenseDetails?.vat_amount,
                      currencyCode: expenseDetails?.store?.currency_code,
                    })}
                  </p>
                </div>
                <div>
                  <h5>Total Amount</h5>
                  <p className={styles.expenseDetails__total_amount}>
                    {formatCurrency({
                      value: expenseDetails?.amount,
                      currencyCode: expenseDetails?.store?.currency_code,
                    })}
                  </p>
                </div>
                <div>
                  <h5>Category</h5>
                  <p>{expenseDetails?.category?.name}</p>
                </div>
                <div>
                  <h5>Payment Method</h5>
                  <p>{expenseDetails?.payment_method}</p>
                </div>
                <div>
                  <h5>Expense Date</h5>
                  <p>{defaultDateDisplay(expenseDetails?.expense_date)}</p>
                </div>
                <div>
                  <h5>Expense Description</h5>
                  <p>
                    {expenseDetails?.description === ""
                      ? "N/A"
                      : expenseDetails?.description}
                  </p>
                </div>
                <div>
                  <h5>Reason for Declination</h5>
                  <p>{expenseDetails?.decline_reason || "N/A"}</p>
                </div>
                <div>
                  <h5>Requested By</h5>
                  <p>
                    {expenseDetails?.initiated_by?.first_name}{" "}
                    {expenseDetails?.initiated_by?.last_name}
                  </p>
                </div>
                <div>
                  <h5>Approved By</h5>
                  <p>
                    {expenseDetails?.reviewed_by
                      ? `${expenseDetails?.reviewed_by?.first_name} ${expenseDetails?.reviewed_by?.last_name}`
                      : "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {expenseDetails?.is_recurring ||
            expenseDetails?.source === "recurring" ? (
              <div>
                <h4>Recurring Details</h4>
                <div className={styles.expenseDetails__card}>
                  {expenseDetails?.is_recurring && (
                    <>
                      <div>
                        <h5>Frequency</h5>
                        <p className="text-capitalize">
                          {expenseDetails?.recurrence_frequency || "N/A"}
                        </p>
                      </div>
                      <div>
                        <h5>Start Date</h5>
                        <p>
                          {expenseDetails?.recurrence_start_date
                            ? defaultDateDisplay(
                                expenseDetails?.recurrence_start_date,
                              )
                            : "N/A"}
                        </p>
                      </div>
                      <div>
                        <h5>Status</h5>
                        <p>
                          {expenseDetails?.recurrence_cancelled_at
                            ? "Cancelled"
                            : "Active"}
                        </p>
                      </div>
                      {!expenseDetails?.recurrence_cancelled_at && (
                        <div>
                          <h5>&nbsp;</h5>
                          <primaryComponents.Button
                            classNames="btn btn--outline-red smallBtn"
                            onClick={() =>
                              setIsConfirmingCancelRecurrence(true)
                            }
                            isDisabled={isCancellingRecurrence}
                          >
                            Cancel Recurrence
                          </primaryComponents.Button>
                        </div>
                      )}
                    </>
                  )}
                  {expenseDetails?.source === "recurring" && (
                    <div>
                      <h5>Parent Expense</h5>
                      <p>
                        {expenseDetails?.parent ? (
                          <Link
                            className="purple--text"
                            style={{ textDecoration: "underline" }}
                            to={pathConstants.EXPENSES_DETAILS({
                              id: expenseDetails.parent,
                              store_id: expenseDetails?.store?.id,
                            })}
                          >
                            View parent expense
                          </Link>
                        ) : (
                          "N/A"
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {expenseDetails?.make_refund ? (
              <div>
                <h4>Disbursement Details</h4>
                <div className={styles.expenseDetails__card}>
                  <div>
                    <h5>Bank</h5>
                    <p>
                      {expenseDetails?.refund_trx?.bank_name ||
                        expenseDetails?.bank_code}
                    </p>
                  </div>
                  <div>
                    <h5>Account Number</h5>
                    <p>{expenseDetails?.account_number}</p>
                  </div>
                  <div>
                    <h5>Amount</h5>
                    <p>
                      {formatCurrency({
                        value: expenseDetails?.amount,
                        currencyCode: expenseDetails?.store?.currency_code,
                      })}
                    </p>
                  </div>
                  <div>
                    <h5>Date</h5>
                    <p>
                      {expenseDetails?.refunded_at
                        ? defaultDateDisplay(expenseDetails?.refunded_at)
                        : "N/A"}
                    </p>
                  </div>
                </div>
              </div>
            ) : null}
          </secondaryComponents.LoaderHelper>

          <secondaryComponents.SideModal
            isActive={isReviewExpense && !isFetchingExpenseDetails}
            closeModal={() => setIsReviewExpense(false)}
          >
            <secondaryComponents.ExpenseReview
              closeWidget={() => setIsReviewExpense(false)}
              expense={expenseDetails}
              handleFetchExpenses={() => {}}
            />
          </secondaryComponents.SideModal>

          <RoutedModal
            headerTitle="Cancel this recurring expense?"
            isActive={isConfirmingCancelRecurrence}
            width={500}
            closeModal={() => setIsConfirmingCancelRecurrence(false)}
            style={{ height: "auto" }}
          >
            <WarningDialog
              cancelModal={() => setIsConfirmingCancelRecurrence(false)}
              closeModal={() => setIsConfirmingCancelRecurrence(false)}
              handler={handleCancelRecurrence}
              isLoading={isCancellingRecurrence}
            >
              <p>
                This stops future auto-generated entries for this expense. Past
                entries already created will not be affected.
              </p>
            </WarningDialog>
          </RoutedModal>
        </motion.div>
      </secondaryComponents.RequestLoaderTrigger>
    </secondaryComponents.MetaSetter>
  );
};
export default ExpenseDetails;
